import React, { useContext, useMemo } from "react";
import {
  ThemeProvider as BaseThemeProvider,
  ThemeContext,
} from "styled-components";
import { Color } from "@glossy/colors";
import { mixThemes } from "@glossy/theme";
import { GlossyTheme } from "@glossy/theme/types";

export type Themes = {
  surface: GlossyTheme;
  header: GlossyTheme;
  body: GlossyTheme;
  tile: GlossyTheme;
  edition: GlossyTheme;
};

export type ThemeName = keyof Themes;

export type CustomThemes = {
  [key: string]: Color | CustomThemes;
};

const ThemesContext = React.createContext<Themes>(null);

const ROOT_NAME = "surface";

export const GlossyThemeProvider = ({
  children,
  name = ROOT_NAME,
}: {
  children: React.ReactNode;
  name: ThemeName;
}) => {
  const themes = useContext(ThemesContext);
  const previousTheme = useContext(ThemeContext);
  if (!themes || !themes[name]) {
    throw new Error("Please add a valid theme " + name);
  }
  const generatedTheme = useMemo(
    () => mixThemes(previousTheme, themes[name]),
    [name, themes, previousTheme]
  );
  return (
    <BaseThemeProvider theme={generatedTheme}>{children}</BaseThemeProvider>
  );
};

export const ThemesProvider = ({
  children,
  themes,
}: {
  children: React.ReactNode;
  themes: Themes;
}) => {
  if (!themes[ROOT_NAME]) {
    throw new Error(`Please enter a valid ${ROOT_NAME} theme!`);
  }
  return (
    <ThemesContext.Provider value={themes}>
      <BaseThemeProvider theme={themes[ROOT_NAME]}>
        {children}
      </BaseThemeProvider>
    </ThemesContext.Provider>
  );
};
